import { nodeText, layoutRichTexts } from '@/utilities/articles'
import { slugifyHeading } from '@/utilities/slugifyHeading'
import type { Page } from '@/payload-types'

type N = Record<string, any>

const IS_BOLD = 1

export type FaqItem = { question: string; nodes: N[]; answerText: string }

const FAQ_SLUG = /faq|haeufige-fragen/
const SOURCES_SLUG = /quellen/
/** `[S6]` or `[6]` */
const MARKER = /\[S?(\d{1,3})\]/g

const isH = (n: N, ...tags: string[]) => n.type === 'heading' && tags.includes(n.tag)
const headingSlug = (n: N) => slugifyHeading(nodeText(n))

/** Paragraph made only of bold text, used as a question when no H3 is used. */
function isBoldQuestion(n: N): boolean {
  if (n.type !== 'paragraph') return false
  const kids: N[] = n.children || []
  const texts = kids.filter((k) => k.type === 'text' && String(k.text || '').trim())
  return texts.length > 0 && kids.every((k) => k.type === 'text' && (k.format & IS_BOLD || !String(k.text || '').trim()))
}

const isQuestion = (n: N) => isH(n, 'h3') || isBoldQuestion(n)

/** Index range [start, end) of the section body after the H2 matching `slug`. */
function sectionRange(children: N[], slug: RegExp): [number, number] | null {
  const h = children.findIndex((n) => isH(n, 'h2') && slug.test(headingSlug(n)))
  if (h < 0) return null
  let end = children.length
  for (let i = h + 1; i < children.length; i++) {
    if (isH(children[i], 'h1', 'h2')) {
      end = i
      break
    }
  }
  return [h + 1, end]
}

function splitFaq(body: N[]): FaqItem[] {
  const items: FaqItem[] = []
  let cur: FaqItem | null = null
  for (const n of body) {
    if (isQuestion(n)) {
      cur = { question: nodeText(n).trim(), nodes: [], answerText: '' }
      items.push(cur)
    } else if (cur) {
      cur.nodes.push(n)
    }
  }
  return items
    .filter((i) => i.question && i.nodes.length > 0)
    .map((i) => ({ ...i, answerText: i.nodes.map(nodeText).join(' ').replace(/\s+/g, ' ').trim() }))
}

/** FAQ questions of a lexical root (for JSON-LD and the accordion). */
export function extractFaqFromRoot(root: N): FaqItem[] {
  const children: N[] = root.children || []
  const r = sectionRange(children, FAQ_SLUG)
  return r ? splitFaq(children.slice(r[0], r[1])) : []
}

export function extractFaq(layout: Page['layout']): { question: string; answerText: string }[] {
  return layoutRichTexts(layout)
    .flatMap((root) => extractFaqFromRoot(root as N))
    .map(({ question, answerText }) => ({ question, answerText }))
}

export const sourceId = (n: number) => `source-${n}`
export const refId = (n: number, k: number) => `ref-${n}-${k}`

/** Number a source list item stands for: explicit `S6`/`[6]` label, else its position. */
export function sourceNumber(item: N, index: number): number {
  const m = nodeText(item).trim().match(/^\[?S?(\d{1,3})\]?(?=[\s.:)\-–]|$)/)
  return m ? Number(m[1]) : index + 1
}

/** Split a text node on valid source markers into text + `sourceRef` nodes. */
export function splitSourceMarkers(
  node: N,
  valid: Set<number>,
  counts: Map<number, number>,
): N[] | null {
  const text = String(node.text || '')
  MARKER.lastIndex = 0
  if (!MARKER.test(text)) return null
  MARKER.lastIndex = 0
  const out: N[] = []
  let last = 0
  let m: RegExpExecArray | null
  while ((m = MARKER.exec(text))) {
    const n = Number(m[1])
    if (!valid.has(n)) continue
    if (m.index > last) out.push({ ...node, text: text.slice(last, m.index) })
    const k = (counts.get(n) || 0) + 1
    counts.set(n, k)
    out.push({ type: 'sourceRef', n, k, label: m[0].slice(1, -1) })
    last = m.index + m[0].length
  }
  if (!out.length) return null
  if (last < text.length) out.push({ ...node, text: text.slice(last) })
  return out
}

/**
 * Article-only tree transform (mutates `root`): FAQ section -> `faqAccordion` node,
 * source list items get `sourceNo` + `refCount`, inline markers become `sourceRef` nodes.
 */
export function transformArticleTree(root: N): void {
  const children: N[] = root.children || []

  // 1. sources
  const sRange = sectionRange(children, SOURCES_SLUG)
  const valid = new Set<number>()
  const sourceItems = new Map<number, N>()
  if (sRange) {
    for (const n of children.slice(sRange[0], sRange[1])) {
      if (n.type !== 'list') continue
      ;(n.children || []).forEach((li: N, i: number) => {
        const no = sourceNumber(li, i)
        if (valid.has(no)) return
        valid.add(no)
        sourceItems.set(no, li)
        li.sourceNo = no
      })
    }
  }

  // 2. inline markers (outside the sources section)
  const counts = new Map<number, number>()
  if (valid.size) {
    const walk = (node: N) => {
      if (!node.children) return
      const next: N[] = []
      for (const c of node.children) {
        if (c.type === 'text') next.push(...(splitSourceMarkers(c, valid, counts) || [c]))
        else {
          walk(c)
          next.push(c)
        }
      }
      node.children = next
    }
    children.forEach((n, i) => {
      if (sRange && i >= sRange[0] - 1 && i < sRange[1]) return
      walk(n)
    })
    for (const [no, li] of sourceItems) li.refCount = counts.get(no) || 0
  }

  // 3. FAQ accordion (needs >= 2 Q&A, otherwise left as plain headings)
  const fRange = sectionRange(children, FAQ_SLUG)
  if (fRange) {
    const items = splitFaq(children.slice(fRange[0], fRange[1]))
    if (items.length >= 2) {
      root.children = [
        ...children.slice(0, fRange[0]),
        {
          type: 'faqAccordion',
          items: items.map((i) => ({ id: slugifyHeading(i.question), question: i.question, children: i.nodes })),
        },
        ...children.slice(fRange[1]),
      ]
    }
  }
}
